import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { subscriptionService } from "../services";
import { FiCheck, FiX } from "react-icons/fi";
import PaymentModal from "../components/PaymentModal";

const Subscription = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  const fetchSubscription = useCallback(async () => {
    try {
      const response = await subscriptionService.getSubscription();
      setSubscription(response.data.subscription);

      const premiumResponse = await subscriptionService.checkPremiumAccess();
      setIsPremium(premiumResponse.data.isPremium);
    } catch (error) {
      console.error("Error fetching subscription:", error);
      toast.error(t("errors.serverError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const handleUpgrade = async () => {
    try {
      setIsProcessing(true);
      
      // Create simulated payment
      const paymentResponse = await subscriptionService.createSimulatedPayment({
        plan: "premium",
        billingCycle: "monthly",
      });

      if (!paymentResponse.data.success) {
        toast.error("Failed to initiate payment");
        setIsProcessing(false);
        return;
      }

      // Store payment data and show modal
      setPaymentData({
        paymentId: paymentResponse.data.paymentId,
        amount: paymentResponse.data.amount,
      });
      setShowPaymentModal(true);
      setIsProcessing(false);
    } catch (error) {
      console.error("Upgrade error:", error);
      toast.error(error.response?.data?.message || "Error processing payment");
      setIsProcessing(false);
    }
  };

  const handlePaymentSubmit = async (cardData) => {
    try {
      setIsProcessing(true);

      // Execute simulated payment
      const executeResponse = await subscriptionService.executeSimulatedPayment({
        paymentId: paymentData.paymentId,
        plan: "premium",
        billingCycle: "monthly",
        cardData: cardData,
      });

      if (executeResponse.data.success) {
        toast.success("¡Pago realizado correctamente! Bienvenido a Premium");
        setShowPaymentModal(false);
        setPaymentData(null);
        
        // Update user in localStorage with new subscription status
        const user = JSON.parse(localStorage.getItem("user"));
        if (user) {
          user.subscriptionStatus = "premium";
          localStorage.setItem("user", JSON.stringify(user));
        }
        
        fetchSubscription();
        setTimeout(() => {
          navigate("/dashboard");
        }, 2000);
      } else {
        toast.error(executeResponse.data.message || "Pago fallido");
      }
    } catch (error) {
      console.error("Payment execution error:", error);
      
      // Simulate occasional payment failures
      if (error.response?.status === 402) {
        toast.error("Fondos insuficientes. Intenta con otra tarjeta.");
      } else {
        toast.error(error.response?.data?.message || "Error al procesar el pago");
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = async () => {
    if (window.confirm("¿Estás seguro de que quieres cancelar tu suscripción Premium?")) {
      try {
        setIsProcessing(true);
        const response = await subscriptionService.cancelSubscription();
        toast.success(response.data.message || "Suscripción cancelada correctamente");
        fetchSubscription();
      } catch (error) {
        console.error("Cancel error:", error);
        toast.error(t("errors.serverError"));
      } finally {
        setIsProcessing(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="subscription-loading">
        <div className="subscription-loading-spinner"></div>
      </div>
    );
  }

  const plans = [
    {
      name: t("subscription.freePlan"),
      price: "Free",
      features: [
        { name: t("subscription.customInterviews"), included: true },
        { name: t("subscription.voiceInterview"), included: true },
        { name: t("subscription.aiGeneratedQuestions"), included: true },
        { name: t("subscription.viewStatistics"), included: false },
        { name: t("subscription.downloadReports"), included: false },
      ],
      current: subscription?.plan === "free",
      action: isPremium ? "downgrade" : null,
    },
    {
      name: t("subscription.premiumPlan"),
      price: "$9.99",
      period: t("subscription.perMonth"),
      features: [
        { name: t("subscription.customInterviews"), included: true },
        { name: t("subscription.voiceInterview"), included: true },
        { name: t("subscription.aiGeneratedQuestions"), included: true },
        { name: t("subscription.viewStatistics"), included: true },
        { name: t("subscription.downloadReports"), included: true },
      ],
      current: subscription?.plan === "premium",
      action: isPremium ? "cancel" : "upgrade",
    },
  ];

  return (
    <div className="subscription-container">
      <div className="subscription-wrapper">
        <div className="subscription-header">
          <h1 className="subscription-title">{t("subscription.title")}</h1>
        </div>

        {/* Mensaje de cancelación programada */}
        {subscription?.status === "cancelled" && subscription?.daysRemaining > 0 && (
          <div className="alert alert-warning">
            <FiX className="alert-icon" />
            <div className="alert-content">
              <p className="alert-title">Tu suscripción fue cancelada</p>
              <p className="alert-message">
                Seguirás disfrutando de Premium hasta el{" "}
                <strong>{new Date(subscription.finalEndDate).toLocaleDateString("es-ES")}</strong>
              </p>
              <p className="alert-message alert-message-mt">
                <strong>Días restantes: {subscription.daysRemaining}</strong>
              </p>
            </div>
          </div>
        )}

        <div className="plans-grid">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`plan-card ${plan.current ? "current" : ""} ${
                !plan.current && plan.action === "upgrade" ? "premium" : ""
              }`}
            >
              <div className="plan-header">
                <h2 className="plan-name">{plan.name}</h2>
                <div className="plan-price">{plan.price}</div>
                {plan.period && <p className="plan-period">{plan.period}</p>}

                {plan.current && (
                  <div className="current-plan-badge">
                    <p>Current Plan</p>
                    {subscription?.status === "cancelled" && subscription?.daysRemaining > 0 && (
                      <p className="current-plan-badge-small">
                        (Cancela en {subscription.daysRemaining}d)
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="plan-body">
                <button
                  onClick={() => {
                    if (plan.action === "upgrade") {
                      handleUpgrade();
                    } else if (plan.action === "cancel") {
                      handleCancel();
                    }
                  }}
                  disabled={plan.current || !plan.action || isProcessing}
                  className={`plan-button ${
                    plan.current
                      ? "current"
                      : plan.action === "upgrade"
                      ? "upgrade"
                      : "cancel"
                  }`}
                >
                  {isProcessing && (plan.action === "upgrade" || plan.action === "cancel")
                    ? "Procesando..."
                    : plan.current
                    ? "Current Plan"
                    : plan.action === "upgrade"
                    ? t("subscription.upgrade")
                    : "Cancelar Suscripción"}
                </button>

                <ul className="plan-features">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="plan-feature">
                      {feature.included ? (
                        <FiCheck className="plan-feature-icon included" />
                      ) : (
                        <FiX className="plan-feature-icon excluded" />
                      )}
                      <span
                        className={`plan-feature-text ${
                          !feature.included ? "excluded" : ""
                        }`}
                      >
                        {feature.name}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {subscription?.endDate && (
          <div className="alert alert-info">
            <p>
              Subscription ends on:{" "}
              {new Date(subscription.endDate).toLocaleDateString()}
            </p>
          </div>
        )}

        {/* Payment Modal */}
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setPaymentData(null);
          }}
          onConfirm={handlePaymentSubmit}
          amount={paymentData?.amount}
          isProcessing={isProcessing}
        />
      </div>
    </div>
  );
};

export default Subscription;
